import offlineDB from '../indexedDB/offlineDB';
import { readThumbprintPointer } from './thumbprintPointer';

// CSR-specific metadata for lifecycle tracking
interface CSRMetadata {
  id: string;                    // storeId_env_csr
  storeId: string;
  environment: 'dev' | 'essai' | 'prod';
  thumbprint: string;            // Windows Store reference
  status: 'pending' | 'used' | 'deleted';
  createdAt: number;
  usedAt: number | null;         // When certificate was issued
  certificateThumbprint: string | null;  // Link to issued cert
  friendlyName: string;          // MEV_Pending_<storeId>_<env>
  pem: string;                   // CSR PEM data
  synced: boolean;
  lastSync: number | null;
  updatedAt: number;
}

// Certificate metadata with enhanced lifecycle tracking
interface CertificateMetadata {
  id: string;
  storeId: string;
  environment: 'dev' | 'essai' | 'prod';
  certType: 'certificate' | 'csr' | 'publicKey' | 'certifPSI' | 'transHeader' | 'companyInfo' | 'terminal';
  data: any;
  thumbprint?: string;           // Reference to Windows Certificate Store
  status?: 'new' | 'active' | 'archived' | 'deleted'; // Lifecycle status
  friendlyName?: string;         // Friendly name in Windows Store
  deviceId?: string;             // idApprl
  csrThumbprint?: string;        // Link back to CSR that generated this cert
  issuedAt?: number;             // When certificate was issued
  archivedAt?: number | null;    // When certificate was archived
  synced: boolean;
  lastSync: number | null;
  updatedAt: number;
}

function friendlyNameOf(cert: any): string {
  return String(cert?.FriendlyName || '');
}

/** Server-mode certificate in Personal: CloudMEV_DEV_<device id>. */
function isCloudSigningCertificate(name: string, envUpper: string): boolean {
  return name.startsWith(`CloudMEV_${envUpper}_`);
}

/** A signing certificate for this environment. PSI certificates are excluded. */
function isSigningCertificate(cert: any, envUpper: string): boolean {
  const name = friendlyNameOf(cert);
  if (!name || name.startsWith('CloudMEV_PSI_')) return false;
  if (isCloudSigningCertificate(name, envUpper)) return true;
  return name.startsWith('MEV_Key_') && name.endsWith(`_${envUpper}`);
}

function isPsiCertificate(cert: any, envUpper: string): boolean {
  const name = friendlyNameOf(cert);
  if (name === `CloudMEV_PSI_${envUpper}`) return true;
  return name.startsWith('MEV_PSI_') && name.endsWith(`_${envUpper}`);
}

function deviceIdFromFriendlyName(name: string, envUpper: string): string | null {
  const cloud = new RegExp(`^CloudMEV_${envUpper}_(.+)$`).exec(name);
  if (cloud?.[1]) return cloud[1];
  const legacy = /MEV_Key_(.+?)_[A-Z]+$/.exec(name);
  return legacy?.[1] || null;
}

function thumbprintOf(cert: any) {
  return String(cert?.Thumbprint || cert?.thumbprint || '').replace(/\s/g, '').toUpperCase();
}

/** The signing certificate recorded for this store. A missing pointer means this store has no certificate. */
function certificateForStore(storeId: string, env: string, certificates: any[]): any | null {
  const envUpper = String(env || '').toUpperCase();
  const pointer = readThumbprintPointer(storeId, env).replace(/\s/g, '').toUpperCase();
  if (!pointer) return null;
  const signing = (certificates || []).filter((cert: any) => isSigningCertificate(cert, envUpper));
  return signing.find((cert: any) => thumbprintOf(cert) === pointer) || null;
}

function issuedAt(cert: any): number {
  const time = new Date(cert?.NotBefore || '').getTime();
  return Number.isFinite(time) ? time : 0;
}

/** Newest certificate first. NotBefore is the time Windows recorded the certificate as created. */
function byNewestIssued(a: any, b: any): number {
  return issuedAt(b) - issuedAt(a);
}

function logNewestChoice(label: string, ranked: any[]) {
  const chosen = ranked[0];
  if (!chosen) return;
  const older = ranked.slice(1).map((cert) => `${friendlyNameOf(cert)} created ${cert?.NotBefore || 'unknown'}`);
  console.log(
    `[MevCertificateService] ${label}: using ${friendlyNameOf(chosen)}, created ${chosen.NotBefore || 'unknown'}.` +
      (older.length ? ` Older certificates are not used: ${older.join('; ')}` : ' No older certificate.')
  );
}

class MevCertificateService {
  private getKey(storeId: string, env: string, certType: string): string {
    return `${storeId}_${env}_${certType}`;
  }

  /**
   * Local pointer for this store and environment.
   */
  private getThumbprintKey(storeId: string, env: string): string {
    const id = String(storeId || "").trim();
    const scope = String(env || "DEV").toLowerCase();
    return id ? `mev_current_thumbprint_${id}_${scope}` : "";
  }

  /**
   * Update the globally visible thumbprint pointer in localStorage
   */
  private syncThumbprintToPointer(storeId: string, env: string, thumbprint: string | null): void {
    const key = this.getThumbprintKey(storeId, env);
    if (!key) return;
    if (thumbprint) {
      localStorage.setItem(key, thumbprint);
    } else {
      localStorage.removeItem(key);
    }
  }

  /**
   * Save certificate metadata to IndexedDB (thumbprint only, NOT certificate data)
   * Certificate itself is stored exclusively in Windows Certificate Store
   * Private keys are NOT stored here - only thumbprint reference
   */
  async saveCertificate(
    storeId: string,
    env: string,
    certType: string,
    data: any, // This parameter kept for compatibility but certificate data is NOT saved
    thumbprint?: string,
    options: { status?: 'new' | 'active' | 'archived', friendlyName?: string, deviceId?: string } = {}
  ): Promise<void> {
    await offlineDB.init();
    const key = this.getKey(storeId, env, certType);
    const metadata: CertificateMetadata = {
      id: key,
      storeId,
      environment: env as 'dev' | 'essai' | 'prod',
      certType: certType as any,
      data: null, // DO NOT store certificate data - only in Windows Store
      thumbprint,
      status: options.status,
      friendlyName: options.friendlyName,
      deviceId: options.deviceId,
      synced: false,
      lastSync: null,
      updatedAt: Date.now(),
    };

    await offlineDB.put('mevCertificates', metadata);
  }

  /**
   * Get certificate from Windows Certificate Store (single source of truth)
   * PURE: Queries Windows Store directly by environment pattern
   * IndexedDB is NOT used for logic - only for audit trail
   * IMPORTANT: Only retrieves actual certificates, not CSRs
   * Returns the certificate recorded for this store. A missing pointer returns null.
   */
  async getCertificate(storeId: string, env: string, certType: string): Promise<any> {
    console.log(`[MevCertificateService] getCertificate: Querying Windows Store for ${storeId}/${env}/${certType}`);

    // CRITICAL: Only retrieve if it's an actual certificate, not a CSR
    if (certType !== 'certificate') {
      console.log(`[MevCertificateService] Not retrieving for type '${certType}'`);
      return null;
    }

    try {
      // Query Windows Store directly (single source of truth - NO IndexedDB)
      const storeResult = await (window as any).api.invoke('list-mev-certificates');

      if (!storeResult.success) {
        console.error('[MevCertificateService] Failed to query Windows Store:', storeResult.error);
        return null;
      }

      const matchingCert = certificateForStore(storeId, env, storeResult.certificates || []);
      if (!matchingCert) {
        console.warn(`[MevCertificateService] No certificate recorded for ${storeId}/${env}`);
        return null;
      }
      const envUpper = env.toUpperCase();

      console.log(`[MevCertificateService] 🎯 SELECTED CERTIFICATE:`);
      console.log(`    FriendlyName: ${matchingCert.FriendlyName}`);
      console.log(`    Thumbprint: ${matchingCert.Thumbprint}`);
      console.log(`    NotBefore: ${matchingCert.NotBefore}`);
      console.log(`    NotAfter: ${matchingCert.NotAfter}`);

      // Retrieve full certificate from Windows Store
      const thumbprint = matchingCert.Thumbprint;

      // Sync to localStorage for cross-tab cache invalidation
      this.syncThumbprintToPointer(storeId, env, thumbprint);

      const certResult = await (window as any).api.invoke('get-certificate-and-key-from-windows-store', thumbprint);
      if (certResult.success && certResult.certPem) {
        let parsedCert: any = { certif: certResult.certPem };

        const deviceId = deviceIdFromFriendlyName(matchingCert.FriendlyName, envUpper);
        if (deviceId) parsedCert.idapprl = deviceId;

        // Try to get serial from certificate if available
        if (window.api && (window.api as any).parseCertificate) {
          try {
            const certInfo = await (window.api as any).parseCertificate(certResult.certPem);
            if (certInfo.serial) {
              parsedCert.serial = certInfo.serial;
            }
          } catch (err) {
            console.warn('[MevCertificateService] Could not parse certificate for serial:', err);
          }
        }

        return parsedCert;
      }
    } catch (error) {
      console.error('[MevCertificateService] Error querying Windows Store:', error);
    }

    console.log(`[MevCertificateService] No certificate found in Windows Store for ${storeId}/${env}`);
    return null;
  }

  /**
   * Save certificate thumbprint to IndexedDB FOR AUDIT TRAIL ONLY
   * IMPORTANT: This data is NEVER read for operational logic.
   * Windows Store is the single source of truth for all operations.
   * Can be used for CSR (before certificate is issued) or certificate (after issuance)
   */
  async saveCertThumbprint(storeId: string, env: string, thumbprint: string, certType: 'certificate' | 'csr' = 'certificate'): Promise<void> {
    await offlineDB.init();
    const key = this.getKey(storeId, env, certType);

    // Always create/overwrite with fresh data from Windows Store
    const metadata: CertificateMetadata = {
      id: key,
      storeId,
      environment: env as 'dev' | 'essai' | 'prod',
      certType: certType as any,
      data: certType === 'csr' ? {} : null,
      thumbprint,
      synced: false,
      lastSync: null,
      updatedAt: Date.now(),
    };

    await offlineDB.put('mevCertificates', metadata);
    console.log(`[MevCertificateService] ✓ Saved thumbprint to IndexedDB for AUDIT TRAIL (${certType}, ${storeId}/${env})`);
  }

  /**
   * Get active certificate thumbprint ONLY (never returns CSR thumbprint)
   * Use getCSR() if you need CSR thumbprint specifically
   * PURE: Only queries Windows Store (single source of truth)
   * IndexedDB is NOT used for logic - only for audit trail
   * Returns the thumbprint recorded for this store. A missing pointer returns null.
   */
  async getActiveCertThumbprint(storeId: string, env: string): Promise<string | null> {
    console.log(`[MevCertificateService] getActiveCertThumbprint: Querying Windows Store for ${storeId}/${env}`);

    try {
      // Query Windows Store directly (single source of truth - NO IndexedDB)
      const storeResult = await (window as any).api.invoke('list-mev-certificates');

      if (!storeResult.success) {
        console.error('[MevCertificateService] Failed to query Windows Store:', storeResult.error);
        return null;
      }

      const activeCert = certificateForStore(storeId, env, storeResult.certificates || []);
      if (!activeCert) {
        console.warn(`[MevCertificateService] No certificate recorded for ${storeId}/${env}`);
        return null;
      }

      console.log(`[MevCertificateService] 🎯 SELECTED CERTIFICATE:`);
      console.log(`    FriendlyName: ${activeCert.FriendlyName}`);
      console.log(`    Thumbprint: ${activeCert.Thumbprint}`);

      const thumbprint = activeCert.Thumbprint;
      console.log(`[MevCertificateService] Thumbprint: ${thumbprint}`);

      // Sync to localStorage for cross-tab cache invalidation
      this.syncThumbprintToPointer(storeId, env, thumbprint);

      return thumbprint;

    } catch (error) {
      console.error('[MevCertificateService] Error querying Windows Store:', error);
      return null;
    }
  }

  /**
   * Get CSR thumbprint specifically (never returns certificate thumbprint)
   * Use this for CSR-related operations and cleanup
   * PURE: Only queries Windows Store (single source of truth)
   * IndexedDB is NOT used for logic - only for audit trail
   * CRITICAL: If multiple CSRs exist with same friendly name, returns the MOST RECENT one (by NotAfter)
   */
  async getCSRThumbprint(storeId: string, env: string): Promise<string | null> {
    console.log(`[MevCertificateService] getCSRThumbprint: Querying Windows Store for ${storeId}/${env}`);

    try {
      // Query Windows Store directly (single source of truth - NO IndexedDB)
      const storeResult = await (window as any).api.invoke('list-mev-certificates');

      if (!storeResult.success) {
        console.error('[MevCertificateService] Failed to query Windows Store:', storeResult.error);
        return null;
      }

      const allCerts = storeResult.certificates || [];
      // CRITICAL: Use lowercase env to match how CSRs are actually created (globalEnvirn.toLowerCase())
      const expectedFriendlyName = `MEV_Pending_${storeId}_${env}`;

      console.log(`[MevCertificateService] Searching for: ${expectedFriendlyName} (${allCerts.length} total certs)`);

      // Find ALL matching pending CSRs in Windows Store
      const matchingCSRs = allCerts.filter((cert: any) =>
        cert.FriendlyName === expectedFriendlyName
      );

      if (matchingCSRs.length === 0) {
        console.log(`[MevCertificateService] No CSR found in Windows Store`);
        return null;
      }

      // CRITICAL: If multiple CSRs found, use the MOST RECENT one (highest NotAfter date)
      if (matchingCSRs.length > 1) {
        console.warn(`[MevCertificateService] ⚠️ Found ${matchingCSRs.length} duplicate CSRs! Selecting most recent...`);
        matchingCSRs.sort((a: any, b: any) => {
          const dateA = new Date(a.NotAfter).getTime();
          const dateB = new Date(b.NotAfter).getTime();
          return dateB - dateA; // Sort descending (newest first)
        });
      }

      const pendingCSR = matchingCSRs[0]; // Use the most recent one
      console.log(`[MevCertificateService] ✓ Found CSR with thumbprint: ${pendingCSR.Thumbprint}`);

      return pendingCSR.Thumbprint;

    } catch (error) {
      console.error('[MevCertificateService] Error querying Windows Store:', error);
      return null;
    }
  }

  /**
   * Save CSR metadata to IndexedDB
   */
  async saveCSR(
    storeId: string,
    env: string,
    data: {
      pem: string;
      thumbprint: string;
      friendlyName: string;
    }
  ): Promise<void> {
    await offlineDB.init();
    const key = this.getKey(storeId, env, 'csr');
    const metadata: CSRMetadata = {
      id: key,
      storeId,
      environment: env as 'dev' | 'essai' | 'prod',
      thumbprint: data.thumbprint,
      status: 'pending',
      createdAt: Date.now(),
      usedAt: null,
      certificateThumbprint: null,
      friendlyName: data.friendlyName,
      pem: data.pem,
      synced: false,
      lastSync: null,
      updatedAt: Date.now(),
    };

    await offlineDB.put('mevCertificates', metadata);
  }

  /**
   * Get CSR metadata from Windows Store (single source of truth)
   * IndexedDB is NOT used for logic - only for audit trail
   * CRITICAL: If multiple CSRs exist with same friendly name, returns the MOST RECENT one (by NotAfter)
   */
  async getCSR(storeId: string, env: string): Promise<CSRMetadata | null> {
    console.log(`[MevCertificateService] getCSR: Querying Windows Store for ${storeId}/${env}`);

    try {
      // Query Windows Store directly (single source of truth)
      const storeResult = await (window as any).api.invoke('list-mev-certificates');

      if (!storeResult.success) {
        console.error('[MevCertificateService] Failed to query Windows Store:', storeResult.error);
        return null;
      }

      const allCerts = storeResult.certificates || [];
      // CRITICAL: Use lowercase env to match how CSRs are actually created (globalEnvirn.toLowerCase())
      const expectedFriendlyName = `MEV_Pending_${storeId}_${env}`;

      // Find ALL matching pending CSRs in Windows Store
      const matchingCSRs = allCerts.filter((cert: any) =>
        String(cert.FriendlyName || "").toLowerCase() === expectedFriendlyName.toLowerCase()
      );

      if (matchingCSRs.length === 0) {
        console.log(`[MevCertificateService] No pending CSR found in Windows Store for ${expectedFriendlyName}`);
        return null;
      }

      // CRITICAL: If multiple CSRs found, use the MOST RECENT one (highest NotAfter date)
      if (matchingCSRs.length > 1) {
        console.warn(`[MevCertificateService] ⚠️ Found ${matchingCSRs.length} duplicate CSRs! Selecting most recent...`);
        matchingCSRs.sort((a: any, b: any) => {
          const dateA = new Date(a.NotAfter).getTime();
          const dateB = new Date(b.NotAfter).getTime();
          return dateB - dateA; // Sort descending (newest first)
        });
      }

      const pendingCSR = matchingCSRs[0]; // Use the most recent one
      console.log(`[MevCertificateService] ✓ Found pending CSR:`, pendingCSR.FriendlyName, `(Thumbprint: ${pendingCSR.Thumbprint})`);

      // Construct CSRMetadata from Windows Store data
      const csrMetadata: CSRMetadata = {
        id: this.getKey(storeId, env, 'csr'),
        storeId,
        environment: env as 'dev' | 'essai' | 'prod',
        thumbprint: pendingCSR.Thumbprint,
        status: 'pending',
        createdAt: new Date(pendingCSR.NotBefore).getTime(),
        usedAt: null,
        certificateThumbprint: null,
        friendlyName: pendingCSR.FriendlyName,
        pem: '', // CSR PEM not available from Windows Store listing
        synced: false,
        lastSync: null,
        updatedAt: Date.now()
      };

      return csrMetadata;

    } catch (error) {
      console.error('[MevCertificateService] Error querying Windows Store for CSR:', error);
      return null;
    }
  }

  /**
   * Update CSR status in IndexedDB FOR AUDIT TRAIL ONLY
   * When marked as 'used', the CSR is archived with a timestamped key
   * IMPORTANT: This only updates IndexedDB audit trail, not operational logic
   */
  async updateCSRStatus(
    storeId: string,
    env: string,
    status: 'pending' | 'used' | 'deleted',
    certificateThumbprint?: string
  ): Promise<void> {
    console.log(`[MevCertificateService] updateCSRStatus: ${status} for ${storeId}/${env} (AUDIT TRAIL ONLY)`);

    if (status === 'used') {
      // Archive CSR from Windows Store to IndexedDB audit trail
      await this.archiveCSR(storeId, env, certificateThumbprint || '');
      await this.deleteCSR(storeId, env);
    } else if (status === 'deleted') {
      // Just remove from IndexedDB audit trail
      await this.deleteCSR(storeId, env);
    } else {
      // For pending status, ensure it's saved for audit
      await offlineDB.init();
      const key = this.getKey(storeId, env, 'csr');
      const metadata: any = {
        id: key,
        storeId,
        environment: env,
        status,
        updatedAt: Date.now(),
      };
      await offlineDB.put('mevCertificates', metadata);
    }
  }

  /**
   * Check if CSR generation is allowed (prevents multiple pending CSRs)
   * CRITICAL: Verifies against ACTUAL Windows Store, not just IndexedDB
   */
  async canGenerateCSR(storeId: string, env: string): Promise<{
    allowed: boolean;
    reason?: string;
    diagnostics?: {
      indexedDBStatus: string;
      windowsStoreStatus: string;
      pendingCSRs: any[];
      syncIssues: string[];
    };
  }> {
    try {
      console.log(`[CSR Validation] === STARTING VALIDATION FOR ${storeId}/${env} ===`);

      const diagnostics = {
        indexedDBStatus: '',
        windowsStoreStatus: '',
        pendingCSRs: [] as any[],
        syncIssues: [] as string[]
      };

      // STEP 1: Check Windows Store for CSR (NOT IndexedDB - that's only for audit)
      const csrMetaDB = await this.getCSR(storeId, env);
      diagnostics.indexedDBStatus = csrMetaDB
        ? `CSR found: status=${csrMetaDB.status}, thumbprint=${csrMetaDB.thumbprint}`
        : 'No CSR found in Windows Store';
      console.log(`[CSR Validation] Windows Store CSR Check: ${diagnostics.indexedDBStatus}`);

      // STEP 2: Query ACTUAL Windows Certificate Store
      const storeResult = await (window as any).api.invoke('list-mev-certificates');

      if (!storeResult.success) {
        console.error('[CSR Validation] Failed to query Windows Store:', storeResult.error);
        diagnostics.windowsStoreStatus = `Error: ${storeResult.error}`;
        return { allowed: true, reason: 'Warning: Could not verify Windows Store', diagnostics };
      }

      const allCerts = storeResult.certificates || [];
      diagnostics.windowsStoreStatus = `${allCerts.length} MEV certificate(s) found`;
      console.log(`[CSR Validation] Windows Store: ${diagnostics.windowsStoreStatus}`);

      // STEP 3: Find pending CSRs
      // CRITICAL: Use lowercase env to match how CSRs are actually created (globalEnvirn.toLowerCase())
      const pendingPattern = `MEV_Pending_${storeId}_${env}`;
      const pendingCSRs = allCerts.filter((cert: any) =>
        cert.FriendlyName && cert.FriendlyName.startsWith('MEV_Pending_')
      );

      diagnostics.pendingCSRs = pendingCSRs.map((cert: any) => ({
        friendlyName: cert.FriendlyName,
        thumbprint: cert.Thumbprint,
        subject: cert.Subject,
        notAfter: cert.NotAfter
      }));

      console.log(`[CSR Validation] Found ${pendingCSRs.length} pending CSR(s) in Windows Store`);

      // STEP 4: Check for exact match
      const exactMatch = pendingCSRs.find((cert: any) => cert.FriendlyName === pendingPattern);

      // STEP 5: Verify CSR still exists if we found one
      if (csrMetaDB && csrMetaDB.status === 'pending') {
        const existsInStore = await (window as any).api.invoke('verify-certificate-exists', csrMetaDB.thumbprint);

        if (!existsInStore.Exists) {
          const issue = `CSR (${csrMetaDB.thumbprint}) no longer exists in Windows Store - AUTO-CLEANING`;
          diagnostics.syncIssues.push(issue);
          console.warn(`[CSR Validation] ${issue}`);
          await this.deleteCSR(storeId, env);

          if (exactMatch) {
            return {
              allowed: false,
              reason: `Pending CSR in Windows Store:\n• ${exactMatch.FriendlyName}\n• Thumbprint: ${exactMatch.Thumbprint}\n• Expires: ${exactMatch.NotAfter}\n\nDelete from Windows Certificate Store.`,
              diagnostics
            };
          }

          console.log('[CSR Validation] === VALIDATION PASSED (after cleanup) ===');
          return { allowed: true, diagnostics };
        }
      }

      // STEP 6: Final decision
      if (exactMatch) {
        console.log(`[CSR Validation] === VALIDATION FAILED: Pending CSR exists ===`);
        return {
          allowed: false,
          reason: `Pending CSR in Windows Store:\n• Name: ${exactMatch.FriendlyName}\n• Thumbprint: ${exactMatch.Thumbprint}\n• Subject: ${exactMatch.Subject}\n• Expires: ${exactMatch.NotAfter}\n\nUse it or delete it first.`,
          diagnostics
        };
      }

      console.log('[CSR Validation] === VALIDATION PASSED ===');
      return { allowed: true, diagnostics };

    } catch (error) {
      console.error('[CSR Validation] === VALIDATION ERROR ===', error);
      return {
        allowed: false,
        reason: `Validation error: ${error instanceof Error ? error.message : String(error)}`
      };
    }
  }

  /**
   * Get certificate metadata WITHOUT retrieving full certificate from Windows Store
   * PURE: Only queries Windows Store (single source of truth)
   * IndexedDB is NOT used for logic - only for audit trail
   * Returns the certificate recorded for this store. A missing pointer returns null.
   */
  async getCertificateMetadata(storeId: string, env: string): Promise<CertificateMetadata | null> {
    console.log(`[MevCertificateService] getCertificateMetadata: Querying Windows Store for ${storeId}/${env}`);

    try {
      // Query Windows Store directly (single source of truth - NO IndexedDB)
      const storeResult = await (window as any).api.invoke('list-mev-certificates');

      if (!storeResult.success) {
        console.error('[MevCertificateService] Failed to query Windows Store:', storeResult.error);
        return null;
      }

      const envUpper = env.toUpperCase();
      const activeCert = certificateForStore(storeId, env, storeResult.certificates || []);
      if (!activeCert) {
        console.log(`[MevCertificateService] No certificate recorded for ${storeId}/${env}`);
        return null;
      }

      console.log(`[MevCertificateService] 🎯 SELECTED CERTIFICATE: ${activeCert.FriendlyName}`);

      const deviceId = deviceIdFromFriendlyName(activeCert.FriendlyName, envUpper);

      return {
        id: this.getKey(storeId, env, 'certificate'),
        storeId,
        environment: env as 'dev' | 'essai' | 'prod',
        certType: 'certificate' as any,
        data: null,
        thumbprint: activeCert.Thumbprint,
        status: 'active',
        friendlyName: activeCert.FriendlyName,
        deviceId: deviceId,
        synced: false,
        lastSync: null,
        updatedAt: Date.now(),
      };

    } catch (error) {
      console.error('[MevCertificateService] Error querying Windows Store:', error);
      return null;
    }
  }

  /**
   * Archive a CSR to IndexedDB with timestamp (for audit trail ONLY)
   * IMPORTANT: This is for historical record keeping only, not for operational logic.
   */
  async archiveCSR(storeId: string, env: string, certificateThumbprint: string): Promise<void> {
    await offlineDB.init();

    // Get the CSR from Windows Store (source of truth)
    const csrData = await this.getCSR(storeId, env);

    if (csrData) {
      const archivedKey = `${storeId}_${env}_csr_archived_${Date.now()}`;
      const archivedCSR: CSRMetadata = {
        ...csrData,
        id: archivedKey,
        status: 'used',
        usedAt: Date.now(),
        certificateThumbprint,
        updatedAt: Date.now(),
      };

      await offlineDB.put('mevCertificates', archivedCSR);
      console.log(`[MevCertificateService] ✓ Archived CSR to IndexedDB for AUDIT TRAIL: ${archivedKey}`);
    } else {
      console.warn(`[MevCertificateService] No CSR found in Windows Store to archive for ${storeId}/${env}`);
    }
  }

  /**
   * Delete active CSR entry from IndexedDB (audit trail cleanup only)
   * IMPORTANT: This only removes the active CSR entry from IndexedDB.
   * Archived CSRs are preserved for audit trail.
   */
  async deleteCSR(storeId: string, env: string): Promise<void> {
    await offlineDB.init();
    const key = this.getKey(storeId, env, 'csr');
    await offlineDB.delete('mevCertificates', key);
    console.log(`[MevCertificateService] ✓ Deleted active CSR from IndexedDB AUDIT TRAIL: ${key}`);
  }

  /**
   * Clean up ALL pending CSRs for a given storeId/env from Windows Store
   * CRITICAL: Use before generating a new CSR to prevent duplicate accumulation
   * Returns number of CSRs deleted
   */
  async cleanupAllPendingCSRs(storeId: string, env: string): Promise<number> {
    console.log(`[MevCertificateService] cleanupAllPendingCSRs: Cleaning up all pending CSRs for ${storeId}/${env}`);

    try {
      // Query Windows Store for all pending CSRs
      const storeResult = await (window as any).api.invoke('list-mev-certificates');

      if (!storeResult.success) {
        console.error('[MevCertificateService] Failed to query Windows Store:', storeResult.error);
        return 0;
      }

      const allCerts = storeResult.certificates || [];
      // CRITICAL: Use lowercase env to match how CSRs are actually created (globalEnvirn.toLowerCase())
      const expectedFriendlyName = `MEV_Pending_${storeId}_${env}`;

      // Find ALL matching pending CSRs
      const matchingCSRs = allCerts.filter((cert: any) =>
        cert.FriendlyName === expectedFriendlyName
      );

      if (matchingCSRs.length === 0) {
        console.log(`[MevCertificateService] No pending CSRs to clean up`);
        return 0;
      }

      console.log(`[MevCertificateService] ⚠️ Found ${matchingCSRs.length} pending CSR(s) to clean up`);

      let deletedCount = 0;
      for (const csr of matchingCSRs) {
        try {
          // Verify it's a pending CSR before deleting
          const verifyResult = await (window as any).api.invoke('verify-certificate-exists', csr.Thumbprint);
          if (verifyResult.Exists && verifyResult.FriendlyName && verifyResult.FriendlyName.startsWith('MEV_Pending_')) {
            await (window as any).api.invoke('delete-certificate-from-windows-store', csr.Thumbprint);
            deletedCount++;
            console.log(`[MevCertificateService] ✓ Deleted old CSR: ${csr.Thumbprint}`);
          }
        } catch (deleteError) {
          console.warn(`[MevCertificateService] Failed to delete CSR ${csr.Thumbprint}:`, deleteError);
        }
      }

      console.log(`[MevCertificateService] ✓ Cleanup complete. Deleted ${deletedCount} CSR(s)`);
      return deletedCount;

    } catch (error) {
      console.error('[MevCertificateService] Error during CSR cleanup:', error);
      return 0;
    }
  }

  /**
   * Update certificate status in IndexedDB FOR AUDIT TRAIL ONLY
   * IMPORTANT: This only updates IndexedDB audit trail, not operational logic
   * Windows Store is the single source of truth for all operations
   */
  async updateCertificateStatus(
    storeId: string,
    env: string,
    status: 'new' | 'active' | 'archived' | 'deleted'
  ): Promise<void> {
    await offlineDB.init();
    const key = this.getKey(storeId, env, 'certificate');

    console.log(`[MevCertificateService] updateCertificateStatus: ${status} for ${storeId}/${env} (AUDIT TRAIL ONLY)`);

    // Create/update audit trail entry (don't read existing, just write fresh)
    const metadata: any = {
      id: key,
      storeId,
      environment: env,
      certType: 'certificate',
      status,
      updatedAt: Date.now(),
    };

    if (status === 'archived') {
      metadata.archivedAt = Date.now();
    }

    await offlineDB.put('mevCertificates', metadata);
  }

  /**
   * Restore from Firestore (fallback)
   * TODO: Implement Firestore restore logic
   */
  private async restoreFromFirestore(storeId: string, env: string, certType: string): Promise<any> {
    // Implementation: Query Firestore stores/{storeId}/mevCertificates
    // Filter by environment and certType
    // Save to IndexedDB after retrieval
    // Return data
    console.warn(`[MevCertificateService] Firestore restore not yet implemented for ${storeId}/${env}/${certType}`);
    return null;
  }

  /**
   * Get PSI certificate thumbprint from Windows Store
   * PSI certificates are stored with friendly name: MEV_PSI_<deviceId>_<ENV>
   * These are PUBLIC certificates only (no private key) used for QR code encryption
   */
  async getPSICertificateThumbprint(storeId: string, env: string): Promise<{ thumbprint: string | null, friendlyName: string | null }> {
    console.log(`[MevCertificateService] getPSICertificateThumbprint: Querying Windows Store for ${storeId}/${env}`);

    try {
      const storeResult = await (window as any).api.invoke('list-mev-certificates');

      if (!storeResult.success) {
        console.error('[MevCertificateService] Failed to query Windows Store:', storeResult.error);
        return { thumbprint: null, friendlyName: null };
      }

      const allCerts = storeResult.certificates || [];
      const envUpper = env.toUpperCase();

      const psiCerts = allCerts.filter((cert: any) => isPsiCertificate(cert, envUpper));
      psiCerts.sort(byNewestIssued);
      logNewestChoice('PSI certificate', psiCerts);

      if (psiCerts.length === 0) {
        console.log(`[MevCertificateService] No PSI certificate found for ${envUpper}`);
        return { thumbprint: null, friendlyName: null };
      }

      // Use the first (should only be one per environment)
      const psiCert = psiCerts[0];
      console.log(`[MevCertificateService] ✓ Found PSI certificate: ${psiCert.FriendlyName}, thumbprint: ${psiCert.Thumbprint}`);

      return {
        thumbprint: psiCert.Thumbprint,
        friendlyName: psiCert.FriendlyName
      };
    } catch (error) {
      console.error('[MevCertificateService] Error querying Windows Store for PSI cert:', error);
      return { thumbprint: null, friendlyName: null };
    }
  }

  /**
   * After a first enrollment (device ID 0000-0000-0000), keep only the new
   * workstation certificate and its PSI certificate for this environment.
   */
  async removeOtherWorkstationCertificates(env: string, keepFriendlyNames: string[]): Promise<string[]> {
    const envUpper = env.toUpperCase();
    const keep = new Set(keepFriendlyNames.map((name) => name.toLowerCase()));
    const storeResult = await (window as any).api.invoke('list-mev-certificates');
    if (!storeResult?.success) return [];
    const removed: string[] = [];
    for (const cert of storeResult.certificates || []) {
      const name = friendlyNameOf(cert);
      const isWorkstationKey = name.startsWith('MEV_Key_') && name.endsWith(`_${envUpper}`);
      const isWorkstationPsi = name.startsWith('MEV_PSI_') && name.endsWith(`_${envUpper}`);
      if ((!isWorkstationKey && !isWorkstationPsi) || keep.has(name.toLowerCase())) continue;
      const thumbprint = String(cert.Thumbprint || '');
      if (!thumbprint) continue;
      const deleted = await (window as any).api.invoke('delete-certificate-from-windows-store', thumbprint);
      if (deleted?.success === false) {
        console.warn(`[MevCertificateService] Could not remove older certificate ${name}`);
        continue;
      }
      removed.push(name);
      console.log(`[MevCertificateService] Removed older certificate ${name}, created ${cert.NotBefore || 'unknown'}`);
    }
    return removed;
  }
}

const mevCertService = new MevCertificateService();
export default mevCertService;

